import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fifoService } from '../../services/fifoService';
import { toast } from 'react-toastify';
import PageLoader from '../../components/PageLoader';
import { API_URL } from '../../config/api';

const RequestDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchRequestDetail();
    }, [id]);

    const fetchRequestDetail = async () => {
        setLoading(true);
        try {
            const data = await fifoService.getAllRequests();
            const foundRequest = data.find(req => req.id === id);
            if (foundRequest) {
                setRequest(foundRequest);
            } else {
                toast.error('ไม่พบใบแจ้งงาน QC');
                navigate('/all-requests');
            }
        } catch (error) {
            toast.error('ไม่สามารถโหลดข้อมูลได้');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = () => {
        // Public endpoint — no token needed, same as the static /pdfs/*.pdf files
        window.open(`${API_URL}/fifo/requests/${id}/pdf`, '_blank');
    };

    const handleCancel = async () => {
        if (!window.confirm(`ยืนยันยกเลิกใบแจ้งงาน ${id}? เลขที่เอกสารจะยังคงอยู่ แต่จะถูกทำเครื่องหมายว่ายกเลิกแล้ว`)) return;
        try {
            await fifoService.cancelRequest(id);
            toast.success('ยกเลิกใบแจ้งงานแล้ว');
            fetchRequestDetail();
        } catch (error) {
            toast.error(error.response?.data?.error || 'ยกเลิกไม่สำเร็จ');
        }
    };

    if (loading) {
        return <PageLoader message="กำลังโหลดข้อมูล..." />;
    }

    if (!request) {
        return null;
    }

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <button
                        className="btn btn-link text-muted p-0 mb-2"
                        onClick={() => navigate('/all-requests')}
                    >
                        <i className="bi bi-arrow-left me-2"></i>
                        กลับไปยังรายการทั้งหมด
                    </button>
                    <h3 className="mb-2">
                        <i className="bi bi-file-earmark-text-fill me-2 text-primary"></i>
                        ใบแจ้งงาน QC #{request.id}
                    </h3>
                    <p className="text-muted mb-0">
                        สร้างเมื่อ {new Date(request.createdAt).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    {request.status === 'Pending' && (
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => navigate(`/edit-request/${id}`)}
                        >
                            <i className="bi bi-pencil-square me-2"></i>
                            แก้ไข
                        </button>
                    )}
                    {!['Cancelled', 'Pass QC', 'Fail QC'].includes(request.status) && (
                        <button
                            className="btn btn-outline-danger"
                            onClick={handleCancel}
                        >
                            <i className="bi bi-x-circle me-2"></i>
                            ยกเลิก
                        </button>
                    )}
                    <button
                        className="btn btn-danger"
                        onClick={handleDownloadPDF}
                    >
                        <i className="bi bi-file-pdf me-2"></i>
                        ดู PDF
                    </button>
                    {(() => {
                        const status = request.status || 'Unknown';
                        let badgeClass = 'bg-secondary';
                        let iconClass = 'bi-circle';

                        if (status === 'Pending') {
                            badgeClass = 'bg-warning text-dark';
                            iconClass = 'bi-clock-history';
                        } else if (status === 'Pass QC') {
                            badgeClass = 'bg-success';
                            iconClass = 'bi-check-circle';
                        } else if (status === 'Fail QC') {
                            badgeClass = 'bg-danger';
                            iconClass = 'bi-x-circle';
                        } else if (status === 'Sent to Qcm' || status === 'At QCM') {
                            badgeClass = 'bg-info text-dark';
                            iconClass = 'bi-arrow-right-circle';
                        } else if (status === 'Cancelled') {
                            badgeClass = 'bg-secondary';
                            iconClass = 'bi-x-circle';
                        }

                        return (
                            <span className={`badge ${badgeClass} fs-6 px-3 py-2`}>
                                <i className={`bi ${iconClass} me-2`}></i>
                                {status}
                            </span>
                        );
                    })()}
                </div>
            </div>

            <div className="row">
                {/* Customer Information */}
                <div className="col-md-6 mb-4">
                    <div className="theme-card h-100">
                        <div className="theme-card-header">
                            <h5 className="mb-0">
                                <i className="bi bi-person-fill me-2"></i>
                                ข้อมูลลูกค้า
                            </h5>
                        </div>
                        <div className="theme-card-body">
                            <div className="mb-3">
                                <label className="text-muted small">ชื่อลูกค้า</label>
                                <div className="fw-medium">{request.customerName || '-'}</div>
                            </div>
                            <div className="mb-3">
                                <label className="text-muted small">ที่อยู่</label>
                                <div className="fw-medium">{request.customerAddress || '-'}</div>
                            </div>
                            <div>
                                <label className="text-muted small">วันที่ต้องการ</label>
                                <div className="fw-medium">
                                    {request.wantDate ? new Date(request.wantDate).toLocaleDateString('th-TH', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    }) : '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Job Details */}
                <div className="col-md-6 mb-4">
                    <div className="theme-card h-100">
                        <div className="theme-card-header">
                            <h5 className="mb-0">
                                <i className="bi bi-list-check me-2"></i>
                                รายการงาน
                            </h5>
                        </div>
                        <div className="theme-card-body">
                            {Array.isArray(request.jobDetails) && request.jobDetails.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                    {request.jobDetails.map((job, idx) => (
                                        <li key={idx} className="mb-2">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            {job}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted mb-0">ไม่มีรายการงาน</p>
                            )}

                            {request.remark && (
                                <div className="mt-4 pt-3 border-top">
                                    <label className="text-muted small">หมายเหตุ</label>
                                    <div className="fw-medium">{request.remark}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Items List */}
            <div className="theme-card">
                <div className="theme-card-header">
                    <h5 className="mb-0">
                        <i className="bi bi-cpu me-2"></i>
                        รายการเครื่อง ({request.items?.length || 0} เครื่อง)
                    </h5>
                </div>
                <div className="theme-card-body p-0">
                    <div className="table-responsive">
                        <table className="table theme-table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th style={{ width: '5%' }}>#</th>
                                    <th style={{ width: '30%' }}>Serial Number</th>
                                    <th style={{ width: '20%' }}>รหัสสินค้า</th>
                                    <th style={{ width: '35%' }}>รายละเอียด</th>
                                    <th style={{ width: '10%' }} className="text-center">สถานะ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {request.items && request.items.length > 0 ? (
                                    request.items.map((item, idx) => (
                                        <tr key={idx}>
                                            <td className="text-muted">{idx + 1}</td>
                                            <td className="fw-medium">
                                                <i className="bi bi-cpu me-2 text-primary"></i>
                                                {item.serial}
                                            </td>
                                            <td className="text-muted">{item.itemCode || '-'}</td>
                                            <td className="text-muted">{item.description || '-'}</td>
                                            <td className="text-center">
                                                <span className="badge bg-success">
                                                    <i className="bi bi-check-lg"></i>
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4 text-muted">
                                            ไม่มีรายการเครื่อง
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequestDetail;
